import {
  DEFAULT_OIDC_STATE,
  HereAccount,
  OIDC_STATE_PROPERTIES,
  OidcState,
  OidcStateKey,
} from '../data/reducer'
import { signIn } from './HereAccountService'
import { TopWindowMessage } from './types'

const NONCE_STORAGE_KEY = 'here_web:account:nonce'

export async function createNonce(): Promise<string> {
  const randomBytes = crypto.getRandomValues(new Uint8Array(32))
  const hashBuffer = await crypto.subtle.digest('SHA-256', randomBytes.buffer)
  return base64Encode(toCharCodes(new Uint8Array(hashBuffer)))
    .substring(0, 16)
    .replace(/[+/]/g, 'A')
}

export function parseNonce(idToken: string): string {
  const parts = idToken.split('.')
  if (parts.length !== 3) {
    return ''
  }

  return parseJson(base64Decode(parts[1]), { nonce: '' }).nonce
}

export function removeNonce(): void {
  localStorage.removeItem(NONCE_STORAGE_KEY)
}

export function storeNonce(nonce: string): void {
  localStorage.setItem(NONCE_STORAGE_KEY, nonce)
}

export function isNonceValid(nonce: string): boolean {
  const storedNonce = localStorage.getItem(NONCE_STORAGE_KEY)
  return storedNonce === nonce
}

export function encodeOidcState(state: OidcState): string {
  return base64Encode(toBinary(JSON.stringify(state)))
}

export function decodeOidcState(encodedState: string): OidcState {
  const state = parseJson(fromBinary(base64Decode(encodedState)), DEFAULT_OIDC_STATE)
  const validatedState: OidcState = { ...DEFAULT_OIDC_STATE }

  Object.entries(state).forEach(([key, value]) => {
    if (isOfType(key)) {
      validatedState[key] = value
    }
  })

  return validatedState
}

function base64Decode(value: string): string {
  let decodedValue
  try {
    decodedValue = atob(value)
  } catch (_) {
    decodedValue = ''
  }

  return decodedValue
}

function base64Encode(value: string): string {
  return btoa(value)
}

function isOfType(value: string): value is OidcStateKey {
  return OIDC_STATE_PROPERTIES.includes(value as OidcStateKey)
}

function toCharCodes(charCodes: Uint16Array | Uint8Array): string {
  let result = ''
  charCodes.forEach((char) => {
    result += String.fromCharCode(char)
  })
  return result
}

function fromBinary(binary: string): string {
  const bytes = Uint8Array.from({ length: binary.length }, (element, index) =>
    binary.charCodeAt(index)
  )
  return toCharCodes(new Uint16Array(bytes.buffer))
}

function toBinary(string: string): string {
  const codeUnits = Uint16Array.from({ length: string.length }, (element, index) =>
    string.charCodeAt(index)
  )

  return toCharCodes(new Uint8Array(codeUnits.buffer))
}

function parseJson<T>(json: string, fallback: T): T {
  let value

  try {
    value = JSON.parse(json) as T
  } catch (_) {
    value = fallback
  }

  return value
}

export function createInitials(user: HereAccount): string {
  const { given_name = '', family_name = '' } = user
  const names = []

  const givenName = given_name.split(' ', 1)
  const familyName = family_name.split(' ', 1)
  if (givenName[0] !== '') {
    names.push(givenName[0])
  }
  if (familyName[0] !== '') {
    names.push(familyName[0])
  }
  return names.join(' ')
}

export function createStartStopMessage(user?: HereAccount): TopWindowMessage {
  return user
    ? {
        action: 'start',
        expires: user.expires,
        source: 'top-window',
      }
    : { action: 'stop', source: 'top-window' }
}

export function createRefreshMessage(): TopWindowMessage {
  return { action: 'refresh', source: 'top-window' }
}

export function createTimeout(callback: () => void, interval: number, id?: number): number {
  if (id !== undefined) {
    clearTimeout(id)
  }
  return window.setTimeout(callback, interval)
}

export function handleTopWindowMessage(
  message: TopWindowMessage,
  currentTimoutId?: number
): number | undefined {
  const { source, action } = message
  if (source !== 'top-window') {
    return
  }

  let timeoutId = undefined
  if (action === 'refresh' && currentTimoutId === undefined) {
    signIn('', 'none')
  } else if (action === 'start') {
    const triggerIn = Math.floor((message.expires - Date.now()) * 0.9)
    timeoutId = createTimeout(() => signIn('', 'none'), triggerIn, currentTimoutId)
  } else if (action === 'stop') {
    clearTimeout(currentTimoutId)
  }

  return timeoutId
}
